import * as React from 'react';
import { Alert, Backdrop, Box, CircularProgress, Divider, Snackbar, Stack, Typography } from '@mui/material';
import {
	ICurrencyRateConfigurationChangeLogList,
	ICurrencyRateConfigurationList,
	ICurrencyRateConfigurationUpdate,
	ICurrencyRateList,
} from '../../../types';
import { CurrencyRatesTable } from '../CurrencyRateTable';
import { CurrencyRateConfigurationsTable } from '../CurrencyRateConfigurationTable';
import { useEffect, useState } from 'react';
import { ShowAlertState } from '../../../../common/types';
import { defaultAlertState } from '../../../../common/constants';
import makeStyles from '@mui/styles/makeStyles/makeStyles';
import {
	getCurrencyRatesConfigurations,
	getCurrencyRatesConfigurationsChangeLog,
	getLatestCurrencyRates,
	updateCurrencyRateConfiguration,
} from '../../../services';
import { extractErrorMessage } from '../../../../quotation/helpers';
import CurrencyRateConfigurationEditModal from '../CurrencyRateConfigurationEditModal';
import CurrencyRateConfigurationChangeLogsModal from '../CurrencyRateConfigurationChangeLogsModal';
import { normalizeToYmd, toLocalYmdFromDate } from '../helpers';

const useStyles = makeStyles((theme) => ({
	backdrop: {
		zIndex: theme.zIndex.modal + 1,
		color: '#fff',
	},
}));

export const CurrencyRatesAdminSection: React.FC = () => {
	const [rates, setRates] = React.useState<ICurrencyRateList[]>([]);
	const [configs, setConfigs] = React.useState<ICurrencyRateConfigurationList[]>([]);
	const [ratesLoading, setRatesLoading] = React.useState<boolean>(false);
	const [configsLoading, setConfigsLoading] = React.useState<boolean>(false);
	const [logsLoading, setLogsLoading] = React.useState<boolean>(false);
	const [saving, setSaving] = React.useState<boolean>(false); // <-- para mostrar Backdrop al guardar
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [configToEdit, setConfigToEdit] = React.useState<ICurrencyRateConfigurationList | null>(null);
	const [configToShowLog, setConfigToShowLog] = React.useState<ICurrencyRateConfigurationList | null>(null);
	const [showModalEditConfiguration, setShowModalEditConfiguration] = React.useState<boolean>(false);
	const [showModalConfigurationChangeLogs, setShowModalConfigurationChangeLogs] = React.useState<boolean>(false);
	const [logsData, setLogsData] = useState<ICurrencyRateConfigurationChangeLogList[]>([]);

	const classes = useStyles();

	const loadRates = async () => {
		setRatesLoading(true);
		try {
			const result = await getLatestCurrencyRates();
			setRates(result.data);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al recibir el listado de tasas de cambio'),
			});
		} finally {
			setRatesLoading(false);
		}
	};

	const loadRatesConfigurations = async () => {
		setConfigsLoading(true);
		try {
			const result = await getCurrencyRatesConfigurations();
			setConfigs(result.data);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(
					error,
					'Ocurrió un error al recibir el listado de configuración de tasas de cambio',
				),
			});
		} finally {
			setConfigsLoading(false);
		}
	};

	const loadChangeLogs = async (coinId: number) => {
		setLogsLoading(true);
		try {
			const result = await getCurrencyRatesConfigurationsChangeLog(coinId);
			setLogsData(result.data);
			setShowModalConfigurationChangeLogs(true);
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(
					error,
					'Ocurrió un error al recibir el listado de logs de cambios en la configuración',
				),
			});
		} finally {
			setLogsLoading(false);
		}
	};

	useEffect(() => {
		loadRates();
		loadRatesConfigurations();
	}, []);

	const handleClickEditConfiguration = (row: ICurrencyRateConfigurationList) => {
		setConfigToEdit(row);
		setShowModalEditConfiguration(true);
	};

	const handleClickShowLogsConfiguration = (row: ICurrencyRateConfigurationList) => {
		setConfigToShowLog(row);
		loadChangeLogs(row.coinId);
	};

	const editBaseRate = React.useMemo(() => {
		if (!configToEdit) return undefined;
		const todayYMD = toLocalYmdFromDate(new Date());
		const match = rates.find((r) => r.coinCode === configToEdit.coinCode && normalizeToYmd(r.date) === todayYMD);
		return match?.baseRate;
	}, [configToEdit, rates]);

	const editEffectiveRate = React.useMemo(() => {
		if (!configToEdit) return undefined;
		const todayYMD = toLocalYmdFromDate(new Date());
		const match = rates.find((r) => r.coinCode === configToEdit.coinCode && normalizeToYmd(r.date) === todayYMD);
		return match?.effectiveRate;
	}, [configToEdit, rates]);

	// Cierre del modal de edición
	const handleCloseEditModal = async (result: ICurrencyRateConfigurationUpdate | null) => {
		setConfigToEdit(null);
		setShowModalEditConfiguration(false);
		if (!result) return; // canceló

		try {
			setSaving(true);
			await updateCurrencyRateConfiguration(result);
			await loadRatesConfigurations(); // refrescamos configs
			await loadRates(); // refrescamos rates
			setAlert({ show: true, severity: 'success', message: 'Configuración actualizada correctamente' });
		} catch (error) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'No se pudo actualizar la configuración'),
			});
		} finally {
			setSaving(false);
		}
	};

	const handleCloseShowLogsModal = () => {
		setConfigToShowLog(null);
		setShowModalConfigurationChangeLogs(false);
		setLogsData([]);
	};

	return (
		<>
			<Backdrop className={classes.backdrop} open={ratesLoading || configsLoading || logsLoading || saving}>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Box display="grid" gap={2} mt={2}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					alignItems={{ xs: 'flex-start', sm: 'center' }}
					justifyContent="space-between"
					spacing={1}
				>
					<Typography variant="h5" fontWeight={700}>
						Administración de tasas de cambio
					</Typography>
				</Stack>

				{/* Tabla de rates */}
				<CurrencyRatesTable rows={rates} loading={ratesLoading} />

				{/* Separador visual sutil */}
				<Divider flexItem />

				{/* Tabla de configuraciones */}
				<CurrencyRateConfigurationsTable
					rows={configs}
					loading={configsLoading}
					onEdit={handleClickEditConfiguration}
					onViewLogs={handleClickShowLogsConfiguration}
				/>

				{/* Modal de edición */}
				{configToEdit && showModalEditConfiguration && (
					<CurrencyRateConfigurationEditModal
						open={!!configToEdit && showModalEditConfiguration}
						row={configToEdit}
						baseRate={editBaseRate}
						effectiveRate={editEffectiveRate}
						onClose={handleCloseEditModal}
					/>
				)}

				{configToShowLog && showModalConfigurationChangeLogs && (
					<CurrencyRateConfigurationChangeLogsModal
						open={configToShowLog !== null && showModalConfigurationChangeLogs}
						coinCode={configToShowLog.coinCode}
						logs={logsData}
						onClose={handleCloseShowLogsModal}
					/>
				)}

				<Snackbar open={alert.show} autoHideDuration={1500} onClose={() => setAlert(defaultAlertState)}>
					<Alert variant="filled" severity={alert.severity}>
						{alert.message}
					</Alert>
				</Snackbar>
			</Box>
		</>
	);
};
